import { createScore } from './52-score-games.mjs';
import { random, shuffled } from './53-game-primitives.mjs';
import { BLOCK_SHAPES } from './56-score-board-games.mjs';
import { createPipePuzzle, routedCells } from './57-score-puzzles.mjs';
import { makeGemBoard } from './60-gem-powers.mjs';
import { TIMED_POCKET_CATALOG } from './71-timed-pocket-catalog.mjs';

/** Closed meaningful difficulty bands. At the highest band new seeded boards
 * are expert variations, not a claim of unlimited difficulty or human skill. */
export function timedDifficulty(gameId, level) {
  const n = Math.max(1, Math.min(12, level));
  const at = (values) => values[Math.min(n - 1, values.length - 1)];
  switch (gameId) {
    case 'gem-swap':
      return { colours: at([4, 5, 6]), goal: at([10, 15, 20]), tier: Math.min(n, 3) };
    case 'cluster-pop':
      return { colours: at([4, 5, 6]), goal: at([10, 15, 20]), tier: Math.min(n, 3) };
    case 'block-fit':
      return { shapes: at([5, 7, 9]), goal: at([10, 20, 30]), tier: Math.min(n, 3) };
    case 'soft-merge':
      return { target: at([256, 512, 1024, 2048, 4096, 8192]), tier: Math.min(n, 6) };
    case 'colour-path':
      return { pairs: at([4, 3, 2]), tier: Math.min(n, 3) };
    case 'memory-pairs':
      return { pairs: at([4, 6, 8]), tier: Math.min(n, 3) };
    case 'tile-slide':
      return { distance: at([6, 12, 18, 20, 22, 24, 26, 28]), tier: Math.min(n, 8) };
    case 'rotation-loop':
      return { rotations: at([6, 12, 18]), tier: Math.min(n, 3) };
    case 'number-trail':
      return { count: at([9, 12, 16, 20, 24]), tier: Math.min(n, 5) };
    case 'spot-pair':
      return { count: at([9, 12, 16, 20, 24]), tier: Math.min(n, 5) };
    default:
      throw new RangeError('Unknown timed game.');
  }
}
/** Generate one bounded board, consuming only replay RNG. Existing mechanic
 * owners still perform moves; these new v3 initial conditions never alter2.4. */
export function createTimedBoard(state) {
  createScore(state);
  const d = state.data,
    policy = timedDifficulty(state.gameId, state.difficulty);
  switch (state.gameId) {
    case 'gem-swap':
      d.board = makeGemBoard(state, policy.colours);
      d.level = policy.tier;
      break;
    case 'cluster-pop':
      d.board = Array.from({ length: 256 }, () => random(state, policy.colours));
      d.board[1] = d.board[0];
      d.level = policy.tier;
      break;
    case 'block-fit':
      d.tray = Array.from({ length: 3 }, () =>
        structuredClone(BLOCK_SHAPES[random(state, policy.shapes)]),
      );
      d.selected = 0;
      d.piece = d.tray[0];
      d.level = policy.tier;
      break;
    case 'soft-merge':
      break;
    case 'colour-path': {
      const route = routedCells(state, 4),
        lengths = Array(policy.pairs).fill(Math.floor(16 / policy.pairs));
      for (let i = 0; i < 16 % policy.pairs; i++) lengths[i]++;
      let offset = 0;
      d.pairs = lengths.map((length) => {
        const pair = [route[offset], route[offset + length - 1]];
        offset += length;
        return pair;
      });
      d.paths = Array.from({ length: policy.pairs }, () => []);
      break;
    }
    case 'memory-pairs':
      d.board = shuffled(
        state,
        Array.from({ length: policy.pairs * 2 }, (_, i) => Math.floor(i / 2)),
      );
      break;
    case 'tile-slide': {
      const pool = TIMED_POCKET_CATALOG[policy.distance];
      d.board = [...pool[random(state, pool.length)]].map(Number);
      d.scramble = policy.distance;
      break;
    }
    case 'rotation-loop':
      state.data = createPipePuzzle(state, policy.tier).data;
      break;
    case 'number-trail':
      d.board = shuffled(
        state,
        Array.from({ length: policy.count }, (_, i) => i + 1),
      );
      break;
    case 'spot-pair': {
      const pair = random(state, 30),
        others = shuffled(
          state,
          Array.from({ length: 30 }, (_, i) => i).filter((v) => v !== pair),
        ).slice(0, policy.count - 2);
      d.board = shuffled(state, [pair, pair, ...others]);
      break;
    }
  }
  // Each board is one held result. Legacy internal Next is never dispatched.
  if (state.data.stage) state.data.stage = 1;
  state.data.events = [];
  state.completed = false;
  state.policy = policy;
}
